import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { Spin, Alert } from "antd";
import { useTranslation } from "react-i18next";
// Icons
import {
  ProductFilled,
  YoutubeFilled,
  ReadFilled,
  SmileFilled,
  VideoCameraFilled,
} from "@ant-design/icons";
// GraphQL
import GET_SURVEY_QUESTION from "graphql/queries/GetSurveyQuestion";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
// Styles
import {
  Container,
  Question,
  UpperCaseHeading,
  AnswersCard,
  CardContent,
  IconContainer,
  StyledCheckbox,
  ContinueButton,
  ResponsiveGrid,
} from "./MultipleChoicesQuestionStyle";

type IconMapping = {
  [key: number]: JSX.Element;
};

const iconMapping: IconMapping = {
  14: <ReadFilled style={{ fontSize: "24px" }} />,
  15: <ProductFilled style={{ fontSize: "24px" }} />,
  16: <YoutubeFilled style={{ fontSize: "24px" }} />,
  17: <SmileFilled style={{ fontSize: "24px" }} />,
  18: <VideoCameraFilled style={{ fontSize: "24px" }} />,
};

type LanguageId =
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "39"
  | "40"
  | "41"
  | "42"
  | "43"
  | "44";

const languageNames: Record<LanguageId, string> = {
  1: "Mandarin (Simplified) ",
  2: "Tiếng Việt",
  3: "Shanghainese",
  4: "Japanese",
  5: "Korean",
  6: "Cantonese",
  39: "Taiwanese ",
  40: "Mandarin (Traditional) ",
  41: "Russian",
  42: "Italian",
  43: "Spanish",
  44: "French",
};

interface PreviousExperienceProps {
  onChange: (data: { experiences: number[] }) => void;
  createSurveyResponse: (surveyQuestionId: number, answerId: number) => void;
  language: string;
}
const PreviousExperience = ({
  onChange,
  createSurveyResponse,
  language,
}: PreviousExperienceProps) => {
  const [selectedExperiences, setSelectedExperiences] = useState<number[]>([]);
  const { t } = useTranslation("registration_survey");
  const languageName = languageNames[language as LanguageId] || language;

  const { error, loading, data } = useQuery(GET_SURVEY_QUESTION, {
    variables: {
      order: 2,
      surveyType: 0,
    },
  });

  if (loading) return <Spin />;
  if (error) return <Alert message="Error loading question" type="error" />;

  const surveyQuestion = data?.surveyQuestion;

  if (!surveyQuestion)
    return <Alert message="Question not found" type="warning" />;

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    const { value, checked } = e.target;
    setSelectedExperiences((prev) =>
      checked ? [...prev, value] : prev.filter((v) => v !== value)
    );
  };

  const handleSubmit = async () => {
    if (selectedExperiences.length > 0) {
      onChange({ experiences: selectedExperiences });
      await Promise.all(
        selectedExperiences.map(async (experienceId) => {
          createSurveyResponse(surveyQuestion.id, experienceId);
        })
      );
    }
  };

  return (
    <Container>
      <Question>
        <UpperCaseHeading>
          {t(surveyQuestion.questionText.trim(), { language: languageName })}
        </UpperCaseHeading>
      </Question>
      <AnswersCard bodyStyle={{ padding: 0 }}>
        {surveyQuestion.surveyAnswers.map((answer: any) => (
          <ResponsiveGrid key={answer.id}>
            <CardContent
              key={answer.id}
              isSelected={selectedExperiences.includes(answer.id)}
              onClick={() =>
                handleCheckboxChange({
                  target: {
                    value: answer.id,
                    checked: !selectedExperiences.includes(answer.id),
                  },
                } as CheckboxChangeEvent)
              }
            >
              <div className="icon-text-group">
                <IconContainer>{iconMapping[answer.id]}</IconContainer>
                <span>{t(answer.answerText.trim())}</span>
              </div>
              <StyledCheckbox
                checked={selectedExperiences.includes(answer.id)}
                onChange={handleCheckboxChange}
                value={answer.id}
              />
            </CardContent>
          </ResponsiveGrid>
        ))}
        <ContinueButton
          type="primary"
          onClick={handleSubmit}
          disabled={selectedExperiences.length === 0}
        >
          CONTINUE
        </ContinueButton>
      </AnswersCard>
    </Container>
  );
};

export default PreviousExperience;
